import { useCallback } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useDropzone } from "react-dropzone";
import { t, toHumanSize } from "@/i18n-js/instance";
import { useSmOrMdScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { useToast } from "@circle-react-uikit/ToastV2";
import { MediaPickerContent } from "./MediaPickerContent";
import { useMediaPickerModal } from "./MediaPickerModal";
import { useUppyUpload } from "./useUppyUpload";

const MAX_FILE_SIZE = 4 * 1024 * 1024 * 1024;

export const MediaPicker = ({
  onUploadStarted,
  onUploadComplete,
  createEmbed,
}) => {
  const toast = useToast();
  const mediaPickerModal = useMediaPickerModal();
  const isSmOrMdScreen = useSmOrMdScreenMediaQuery();

  const { uppy, isUploading, progress, isProcessingChecksum } = useUppyUpload({
    onUploadComplete,
    onUploadStarted,
    allowedFileTypes: [".mp4", ".mov", ".m4v", ".mp3", ".wav", ".m4a"],
  });

  const handleUpload = files => {
    uppy.getFiles().forEach(file => uppy.removeFile(file.id));
    uppy.addFile(files[0]);
  };

  const handleFileRejection = useCallback(
    errors => {
      const error = errors[0];
      if (error.errors[0].code === "file-too-large") {
        toast.error(
          t("tiptap.file_too_large", { size: toHumanSize(MAX_FILE_SIZE) }),
        );
      } else {
        toast.error(t("courses.edit.lesson.invalid_file"));
      }
    },
    [toast],
  );

  const { getRootProps, getInputProps, isDragAccept, isDragReject, open } =
    useDropzone({
      onDropAccepted: handleUpload,
      onDropRejected: handleFileRejection,
      noClick: true,
      disabled: isUploading,
      accept: {
        "video/*": [".mp4", ".mov", ".m4v"],
        "audio/*": [".mp3", ".wav", ".m4a"],
      },
      maxFiles: 1,
      multiple: false,
      maxSize: MAX_FILE_SIZE,
    });

  const isDragActive = isDragAccept || isDragReject;

  const getPromptText = () => {
    if (isDragAccept) {
      return t("courses.edit.lesson.drop_here");
    }
    if (isDragReject) {
      return t("courses.edit.lesson.unsupported_file");
    }
    return isSmOrMdScreen
      ? t("courses.edit.lesson.upload_prompt")
      : t("courses.edit.lesson.drag_and_drop_prompt");
  };

  return (
    <div
      className={classNames(
        "bg-secondary border-input flex h-auto flex-col items-center justify-center gap-4 rounded-lg border border-dashed px-6 py-3 md:h-[320px]",
        {
          "border-blue-500 bg-blue-50": isDragAccept,
          "cursor-not-allowed border-red-500 bg-red-50": isDragReject,
        },
      )}
      {...getRootProps()}
    >
      <input {...getInputProps()} />
      <MediaPickerContent
        isUploading={isUploading}
        isProcessingChecksum={isProcessingChecksum}
        progress={progress}
        isDragActive={isDragActive}
        promptText={getPromptText()}
        onSelectFile={() =>
          mediaPickerModal.show({
            getRootProps,
            openFilePicker: open,
            createEmbed,
          })
        }
      />
    </div>
  );
};

MediaPicker.propTypes = {
  onUploadComplete: PropTypes.func.isRequired,
  onUploadStarted: PropTypes.func,
};
